import { useRef, useCallback } from 'react';

export interface NavigationState {
    currentInputIndex: number;
    lastFocusedElement: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
    isProgrammaticFocus: boolean;
}

export const useFormNavigation = () => {
    const lastFocusedRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>(null);
    const currentInputIndexRef = useRef<number>(0);
    const isProgrammaticFocusRef = useRef<boolean>(false);

    const getFormInputs = useCallback((): Array<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> => {
        const container = document.getElementById('report');
        if (!container) return [];

        return Array.from(
            container.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
                'input, textarea, select'
            )
        ).filter(el => !el.disabled && el.tabIndex !== -1 && el.offsetParent !== null);
    }, []);

    const updateCurrentInputIndex = useCallback((target: HTMLElement) => {
        if (isProgrammaticFocusRef.current) {
            isProgrammaticFocusRef.current = false;
            return;
        }

        const inputs = getFormInputs();
        const index = inputs.findIndex(el => el === target);
        if (index !== -1) {
            currentInputIndexRef.current = index;
        }
    }, [getFormInputs]);

    const focusNextInput = useCallback((): boolean => {
        const inputs = getFormInputs();
        if (inputs.length === 0) return false;

        // Validate and update current index
        if (currentInputIndexRef.current < 0 || currentInputIndexRef.current >= inputs.length) {
            currentInputIndexRef.current = 0;
        } else {
            currentInputIndexRef.current = (currentInputIndexRef.current + 1) % inputs.length;
        }

        const nextInput = inputs[currentInputIndexRef.current];
        if (!nextInput) return false;

        // Set programmatic focus flag
        isProgrammaticFocusRef.current = true;

        // Focus and position cursor
        nextInput.focus();
        if (nextInput instanceof HTMLInputElement || nextInput instanceof HTMLTextAreaElement) {
            nextInput.setSelectionRange?.(nextInput.value.length, nextInput.value.length);
        }

        lastFocusedRef.current = nextInput;
        return true;
    }, [getFormInputs]);

    const focusPreviousInput = useCallback((): boolean => {
        const inputs = getFormInputs();
        if (inputs.length === 0) return false;

        // Validate and update current index
        if (currentInputIndexRef.current <= 0) {
            currentInputIndexRef.current = inputs.length - 1;
        } else {
            currentInputIndexRef.current = currentInputIndexRef.current - 1;
        }

        const prevInput = inputs[currentInputIndexRef.current];
        if (!prevInput) return false;

        isProgrammaticFocusRef.current = true;
        prevInput.focus();

        if (prevInput instanceof HTMLInputElement || prevInput instanceof HTMLTextAreaElement) {
            prevInput.setSelectionRange?.(prevInput.value.length, prevInput.value.length);
        }

        lastFocusedRef.current = prevInput;
        return true;
    }, [getFormInputs]);

    const setLastFocusedElement = useCallback((element: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null) => {
        lastFocusedRef.current = element;
    }, []);

    const getNavigationState = useCallback((): NavigationState => ({
        currentInputIndex: currentInputIndexRef.current,
        lastFocusedElement: lastFocusedRef.current,
        isProgrammaticFocus: isProgrammaticFocusRef.current
    }), []);

    const focusFirstInput = useCallback(() => {
        const inputs = getFormInputs();
        if (!inputs.length) return false;
        currentInputIndexRef.current = 0;
        inputs[0].focus();
        lastFocusedRef.current = inputs[0];
        return true;
    }, [getFormInputs]);

    const focusLastInput = useCallback(() => {
        const inputs = getFormInputs();
        if (!inputs.length) return false;
        currentInputIndexRef.current = inputs.length - 1;
        const last = inputs.at(-1);
        if (last) last.focus();
        lastFocusedRef.current = last;
        return true;
    }, [getFormInputs]);

    return {
        lastFocusedRef,
        currentInputIndexRef,
        isProgrammaticFocusRef,
        getFormInputs,
        updateCurrentInputIndex,
        focusNextInput,
        focusPreviousInput,
        setLastFocusedElement,
        getNavigationState,
        focusFirstInput,
        focusLastInput,
    };
};
